import * as yup from 'yup';
import moment from 'moment';
import {
  BeneficiaryDTO,
  BeneficiaryType,
  TrustDTO,
  TrusteeType,
  TrustGrantor,
  TrustType,
} from '../../../../api/useTrustsQuery';
import { UpsertTrustBody } from '../../../../api/useUpsertTrustMutation';
import { OptionType } from '../../../../../../CommonComponents/OPL/Select/BaseSelect/types';

interface FormBeneficiary extends Omit<BeneficiaryDTO, 'percentage' | 'id'> {
  id: string | null;
  percentage: number | string;
}

export interface FormValues {
  name: string;
  trustType: TrustType;
  situs: string;
  trustees: { label: string; value: TrusteeType }[];
  datedDate: string;
  grantors: OptionType<TrustGrantor>[];
  isGstExempt: boolean;
  beneficiaries: {
    primary: FormBeneficiary[];
    contingent: FormBeneficiary[];
  };
  lastBeneficiaryUpdate: string;
  includeInGrossEstate: boolean | null;
  percentageAtFirstSpouseDeath: number | null;
  assetsAtFirstSpouseDeath: string[];
  illiquidAssetOptions: { value: string; label: string }[];
}

const dateValidation = (testName: string) =>
  yup
    .string()
    .nullable()
    .test(testName, 'Date is not valid', value => {
      if (!value) {
        return true;
      }

      const date = moment(value, 'MM/DD/YYYY', true);
      return date.isValid();
    });

const beneficiaryValidation = (beneficiaryType: 'primary' | 'contingent') =>
  yup.array().of(
    yup.object().shape({
      name: yup.string().required('Name is required'),
      percentage: yup
        .string()
        .test(`${beneficiaryType}Allocation`, 'Invalid', value => !!value && Number(value) <= 100),
    }),
  );

export const validation = yup.object().shape({
  name: yup.string().required('This field is required'),
  datedDate: dateValidation('is-dated-date-valid'),
  lastBeneficiaryUpdate: dateValidation('is-lastBeneficiaryUpdate-date-valid'),
  beneficiaries: yup.object().shape({
    primary: beneficiaryValidation('primary'),
    contingent: beneficiaryValidation('contingent'),
  }),
});

const parseBeneficiariesToForm = (beneficiaries: BeneficiaryDTO[]) => ({
  primary: beneficiaries.filter(({ type }) => type === BeneficiaryType.Primary),
  contingent: beneficiaries.filter(({ type }) => type === BeneficiaryType.Contingent),
});

export const dataToForm = (trust: Partial<TrustDTO>): FormValues => ({
  name: trust.Name || '',
  trustType: trust.TrustType ?? TrustType.Revocable,
  situs: trust.Situs || '',
  trustees: (trust.Trustees || []).map(option => ({
    value: option.Value,
    label: option.Label,
  })),
  datedDate: trust.DatedDate ? moment.utc(trust.DatedDate).format('MM/DD/YYYY') : '',
  grantors: (trust.Grantors || []).map(option => ({
    value: option.Value,
    label: option.Label,
  })),
  isGstExempt: trust.IsGstExempt ?? false,
  beneficiaries: parseBeneficiariesToForm(
    trust.Beneficiaries?.beneficiaries && trust.Beneficiaries.beneficiaries.length > 0
      ? trust.Beneficiaries.beneficiaries
      : [],
  ),
  lastBeneficiaryUpdate: trust.Beneficiaries?.lastBeneficiaryUpdate
    ? moment.utc(trust.Beneficiaries.lastBeneficiaryUpdate).format('MM/DD/YYYY')
    : '',
  includeInGrossEstate:
    trust.IncludeInGrossEstate !== undefined ? trust.IncludeInGrossEstate : null,
  percentageAtFirstSpouseDeath: trust.LiquidAssetDistributionStrategy
    ? trust.LiquidAssetDistributionStrategy.PercentageAtFirstSpouseDeath
    : null,
  assetsAtFirstSpouseDeath: trust.IlliquidAssetDistributionStrategy
    ? trust.IlliquidAssetDistributionStrategy.AssetsAtFirstSpouseDeath
    : [],
  illiquidAssetOptions: (trust.IlliquidAssetOptions || []).map(({ Id, Name }) => ({
    value: Id,
    label: Name,
  })),
});

interface FormToUpsertTrustBodyParams {
  trustId?: string;
  values: FormValues;
}

export const formToUpsertTrustBody = ({
  trustId,
  values,
}: FormToUpsertTrustBodyParams): UpsertTrustBody => ({
  Id: trustId || '',
  Name: values.name,
  IsGstExempt: values.isGstExempt,
  Grantors: values.grantors,
  TrustType: values.trustType,
  Situs: values.situs,
  Trustees: values.trustees.map(trust => ({
    value: trust.value,
    label: trust.label,
  })),
  DatedDate: values.datedDate,
  Beneficiaries: {
    beneficiaries: [...values.beneficiaries.primary, ...values.beneficiaries.contingent].filter(
      ({ name }) => !!name,
    ) as BeneficiaryDTO[],
    lastBeneficiaryUpdate: values.lastBeneficiaryUpdate || null,
  },
  IncludeInGrossEstate: values.includeInGrossEstate,
  LiquidAssetDistributionStrategy:
    typeof values.percentageAtFirstSpouseDeath === 'number'
      ? {
          PercentageAtFirstSpouseDeath: values.percentageAtFirstSpouseDeath,
        }
      : null,
  IlliquidAssetDistributionStrategy: {
    AssetsAtFirstSpouseDeath: values.assetsAtFirstSpouseDeath,
  },
});
